import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { Plus, FileText, Edit3, Trash2, Copy, CheckCircle2, CircleOff } from 'lucide-react';
import {
  useTenantForms,
  useDeleteForm,
  useCloneForm,
  type TenantForm,
} from '~/lib/forms-queries';
import { useConfirm } from '~/components/ConfirmDialog/ConfirmDialog';
import { CloneDialog } from '~/components/CloneDialog/CloneDialog';
import { Tooltip } from '~/components/Tooltip/Tooltip';
import { useTenantContext } from '~/lib/tenantContext';
import styles from './_authed.automations.templates.module.css';

export const Route = createFileRoute('/_authed/automations/forms')({
  component: FormsListPage,
});

function FormsListPage() {
  const navigate = useNavigate();
  const { tenants, selectedTenantId } = useTenantContext();
  const { data: forms, isLoading } = useTenantForms();
  const del = useDeleteForm();
  const clone = useCloneForm();
  const confirm = useConfirm();
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [cloning, setCloning] = useState<TenantForm | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  function handleClone(targetTenant: string) {
    if (!cloning) return;
    clone.mutate(
      { source: cloning, targetTenant },
      {
        onSuccess: ({ form }) => {
          setCloning(null);
          navigate({ to: '/automations/forms/$formId', params: { formId: form.id } });
        },
      },
    );
  }

  // Filtra siempre por el tenant activo del contexto (los forms son per-tenant).
  const filtered = useMemo(() => {
    if (!forms) return [];
    if (!selectedTenantId) return [];
    return forms.filter((f) => f.tenant === selectedTenantId);
  }, [forms, selectedTenantId]);

  async function handleDelete(form: TenantForm) {
    const ok = await confirm({
      title: `Eliminar formulario "${form.name}"`,
      description: (
        <>
          El endpoint <code>/api/forms/submit/{form.slug}</code> dejará de
          responder y cualquier sitio que tenga el snippet embebido recibirá
          404. Esta acción no se puede deshacer.
        </>
      ),
      confirmLabel: 'Eliminar',
      tone: 'danger',
    });
    if (!ok) return;
    setDeletingId(form.id);
    setActionError(null);
    del.mutate(form.id, {
      onError: (err) => setActionError('No se pudo eliminar el formulario: ' + ((err as { message?: string }).message || 'error desconocido')),
      onSettled: () => setDeletingId(null),
    });
  }

  return (
    <div className={styles.tab}>
      <div className={styles.tabHeader}>
        <Link to="/automations/forms/new" className={styles.primaryBtn}>
          <Plus size={14} aria-hidden /> Nuevo formulario
        </Link>
      </div>

      {actionError && (
        <p role="alert" style={{ background: 'color-mix(in srgb, #dc2626 12%, transparent)', color: '#b91c1c', padding: '10px 14px', borderRadius: 8, fontSize: 14, margin: '0 0 12px' }}>
          {actionError}
        </p>
      )}

      <div className={styles.toolbar}>
        {forms && (
          <span className={styles.countBadge}>
            {filtered.length} formulario{filtered.length === 1 ? '' : 's'} del cliente activo
          </span>
        )}
      </div>

      {isLoading && <div className={styles.empty}>Cargando formularios…</div>}

      {!isLoading && (!forms || forms.length === 0) && (
        <div className={styles.emptyState}>
          <FileText size={28} aria-hidden style={{ marginBottom: 8, opacity: 0.5 }} />
          <p>
            No hay formularios todavía. Crea el primero con el botón{' '}
            <strong>"Nuevo formulario"</strong>.
          </p>
          <p className="subtle" style={{ marginTop: 8 }}>
            Cada formulario expone un endpoint público{' '}
            <code>/api/forms/submit/&lt;slug&gt;</code> y se puede embeber con un
            snippet HTML o init JS. Las submissions disparan el flow asociado
            (autoresponse, asignar owner, crear task, etc.).
          </p>
        </div>
      )}

      {!isLoading && forms && forms.length > 0 && filtered.length === 0 && (
        <div className={styles.emptyState}>
          <p>No hay formularios que coincidan con el filtro.</p>
        </div>
      )}

      {!isLoading && filtered.length > 0 && (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Nombre</th>
              <th>Flow asociado</th>
              <th>Estado</th>
              <th>Última edición</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {filtered.map((f) => {
              const flowName = f.expand?.default_flow?.name;
              return (
                <tr key={f.id} className={deletingId === f.id ? styles.rowDeleting : ''}>
                  <td>
                    <Tooltip content={<>Slug: <code>{f.slug}</code> · se incrusta solo en el snippet</>}>
                      <Link
                        to="/automations/forms/$formId"
                        params={{ formId: f.id }}
                        className={styles.templateName}
                      >
                        <FileText size={14} aria-hidden style={{ opacity: 0.5 }} />
                        {f.name}
                      </Link>
                    </Tooltip>
                  </td>
                  <td className="subtle">
                    {flowName ? (
                      flowName
                    ) : (
                      <Tooltip content="Sin flow asociado. Las submissions crean el lead pero no disparan automatización específica del form (solo los flows globales con trigger_type='lead_created').">
                        <em>—</em>
                      </Tooltip>
                    )}
                  </td>
                  <td>
                    {f.active ? (
                      <span className={styles.statusActive}>
                        <CheckCircle2 size={12} aria-hidden /> Activo
                      </span>
                    ) : (
                      <Tooltip content="El endpoint público está pausado. POST devolverá 404 form_not_found.">
                        <span className={styles.statusInactive}>
                          <CircleOff size={12} aria-hidden /> Inactivo
                        </span>
                      </Tooltip>
                    )}
                  </td>
                  <td className="subtle">{formatDate(f.updated)}</td>
                  <td className={styles.actions}>
                    <Link
                      to="/automations/forms/$formId"
                      params={{ formId: f.id }}
                      className={styles.iconBtn}
                      title="Editar"
                    >
                      <Edit3 size={14} aria-hidden />
                    </Link>
                    <button
                      type="button"
                      className={styles.iconBtn}
                      onClick={() => setCloning(f)}
                      title="Duplicar"
                      aria-label={`Duplicar formulario ${f.name}`}
                    >
                      <Copy size={14} aria-hidden />
                    </button>
                    <button
                      type="button"
                      className={styles.iconBtn}
                      onClick={() => handleDelete(f)}
                      title="Eliminar"
                      aria-label={`Eliminar formulario ${f.name}`}
                    >
                      <Trash2 size={14} aria-hidden />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {cloning && (
        <CloneDialog
          title="Duplicar formulario"
          itemName={cloning.name}
          sourceTenantId={cloning.tenant}
          tenants={tenants}
          isPending={clone.isPending}
          onClose={() => setCloning(null)}
          onClone={handleClone}
          crossTenantNote={
            <>
              Al copiar a otro cliente, el <strong>flow asociado</strong>{' '}
              (<code>default_flow</code>) queda en blanco — apunta a un flujo del
              cliente origen. Tendrás que asignarle un flujo del cliente destino
              después. El slug se ajusta para no chocar con otros formularios.
            </>
          }
        />
      )}
    </div>
  );
}

function formatDate(iso: string): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('es-CL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}
